/**
 * WorkflowEditorPage Component
 *
 * Visual workflow editor using React Flow.
 * Provides drag-and-drop node creation, connection management,
 * and real-time workflow building.
 */

import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import ReactFlow, {
  Background,
  Controls,
  Panel,
  ReactFlowProvider,
  useReactFlow,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '@/shared/components/ui/Button';
import { Breadcrumb } from '@/shared/components/ui/Breadcrumb';
import { Input } from '@/shared/components/ui/Input';
import { useConfirm } from '@/shared/components/ui/ConfirmProvider';
import {
  useWorkflow,
  useCreateWorkflow,
  useUpdateWorkflow,
  useActivateWorkflow,
  usePauseWorkflow,
  useWorkflowBuilder,
  useValidateWorkflow,
  executionKeys,
} from '../hooks';
import { NodeSelector } from '../components/NodeSelector';
import { NodeConfigPanel } from '../components/config/NodeConfigPanel';
import { calculateAvailableContext } from '../utils/node-schemas';
import { getNodeTypeLabel } from '../utils/node-labels';
import type { NodePreset } from '../presets/node-presets';
import { ExecutionHistorySidebar } from '../components/ExecutionHistorySidebar';
import { WorkflowStatusBadge } from '../components/WorkflowStatusBadge';
import { WorkflowAIPanel } from '../components/WorkflowAIPanel';
import { WorkflowTestPanel } from '../components/WorkflowTestPanel';
import { WorkflowTestModal, TestContext } from '../components/WorkflowTestModal';
import { TriggerPreviewModal } from '../components/TriggerPreviewModal';
import { NodeRunInputModal } from '../components/NodeRunInputModal';
import { getUnrecordedNodeReason } from '../utils/node-envelope-membership';
import { isBelowCancelledOutput } from '../utils/cancelled-branch';
import { formFieldsFilledLater, savedVariablesBefore } from '../utils/saved-variables';
import { BaseNode } from '../components/nodes/BaseNode';
import { useWorkflowTestSocket } from '../hooks/useWorkflowTestSocket';
import { workflowsApi } from '../services/workflowsApi';
import type { WorkflowResponse, CreateWorkflowRequest, UpdateWorkflowRequest } from '../types';
import type { WorkflowDefinition, WorkflowExecutionResponse, WorkflowNodeCancellation } from '@troia-v3/types';
import { WORKFLOW_FAILURE_HANDLE, WORKFLOW_WAIT_CANCELLED_HANDLE, askQuestionOutputHandles } from '@troia-v3/types';
import { Icon } from '@/shared/icons'
import type { IconName } from '@/shared/icons';
// Custom node types — all use BaseNode (Design A Rich Preview)
const nodeTypes = {
  triggerNode: BaseNode,
  actionNode: BaseNode,
  controlNode: BaseNode,
  aiAgentNode: BaseNode,
  aiAgentInlineNode: BaseNode,
};

/**
 * Quanto o nó criado pelo "+" da saída "Cancelado" desce no quadro
 * (spec 2026-09-20 §7.2). Sem o recuo ele nasceria exatamente em cima do nó do
 * lado verde, que ocupa o mesmo x.
 */
const CANCELLED_BRANCH_Y_OFFSET = 160;

/** Por que "Visualizar contexto" fica cinza enquanto o workflow não foi salvo. */
const RUN_INPUTS_DISABLED_REASON = 'Salve o workflow para ver o contexto das execuções.';
/**
 * Por que "Visualizar contexto" fica cinza num nó que ainda não foi salvo.
 *
 * Nó arrastado, colado ou importado agora não está na definição que o servidor
 * conhece: ele não tem como ter contexto, e pedir o contexto dele devolveria
 * uma tela de erro a quem não fez nada de errado. Depois de Salvar o item
 * habilita sozinho — `useUpdateWorkflow` escreve o workflow devolvido no cache
 * do detalhe (`queryClient.setQueryData(workflowKeys.detail(...))` em
 * `hooks/useWorkflows.ts`), e é desse cache que sai a lista abaixo.
 */
const RUN_INPUTS_UNSAVED_NODE_REASON = 'Salve o workflow para ver o contexto deste nó.';

/**
 * Rótulo do nó como a tela tem de chamá-lo: o que a pessoa escreveu; se estiver
 * vazio, o nome do tipo; e, em último caso, "Nó".
 *
 * Uma régua só para a confirmação de exclusão e para o título de "Visualizar
 * contexto". `typeof '' === 'string'` deixava o rótulo apagado passar e o
 * título terminava num travessão solto ("Visualizar contexto — ").
 */
function getNodeDisplayLabel(node: { data?: { label?: unknown; type?: unknown } } | undefined): string {
  const rawLabel: unknown = node?.data?.label;
  if (typeof rawLabel === 'string' && rawLabel.length > 0) return rawLabel;
  const rawType: unknown = node?.data?.type;
  const typeLabel = getNodeTypeLabel(typeof rawType === 'string' ? rawType : '');
  return typeLabel.length > 0 ? typeLabel : 'Nó';
}

/**
 * A tecla Delete/Backspace não apaga nada quando vem de um campo de digitação
 * ou de um menu aberto.
 *
 * As quatro primeiras conferências são a régua do React Flow (`isInputDOMNode`,
 * lida em `node_modules/@reactflow/core/dist/esm/index.mjs`): INPUT, SELECT,
 * TEXTAREA, `[contenteditable]` e qualquer coisa dentro de `.nokey`. Inclusive
 * o jeito de achar o alvo — `composedPath()[0]`, e não `event.target`: num
 * evento que atravessa shadow DOM o `target` chega REAPONTADO para o elemento
 * hospedeiro, então o campo lá de dentro pareceria "não é campo" e o Delete
 * apagaria nós enquanto alguém digita. E a peneira é `Element`, não
 * `HTMLElement`: recusar o que não é HTML deixaria passar alvo de dentro de
 * SVG, que o React Flow trata igual.
 *
 * A quinta conferência é acréscimo NOSSO, que o React Flow não tem: menu do nó
 * aberto — o Radix deixa o foco dentro do menu, e ali a tecla é do menu, não do
 * quadro.
 *
 * Com `deleteKeyCode={null}` o atalho embutido some e esta passa a ser a ÚNICA
 * guarda — a antiga, só com INPUT/TEXTAREA, deixaria um `<select>` focado
 * apagar nós.
 */
function isDeleteKeySilenced(event: KeyboardEvent): boolean {
  const target = event.composedPath?.()?.[0] ?? event.target;
  if (!(target instanceof Element)) return false;
  if (target.tagName === 'INPUT' || target.tagName === 'SELECT' || target.tagName === 'TEXTAREA') return true;
  if (target.hasAttribute('contenteditable')) return true;
  // `isContentEditable` também vale quando a edição é HERDADA de um ancestral,
  // caso em que o próprio elemento não tem o atributo.
  if (target instanceof HTMLElement && target.isContentEditable) return true;
  if (target.closest('.nokey') !== null) return true;
  return target.closest('[role="menu"]') !== null;
}

/**
 * Toolbar component
 */
const EditorToolbar: React.FC<{
  workflow: WorkflowResponse | null;
  mode: 'workflow' | 'skill';
  name: string;
  onNameChange: (name: string) => void;
  onSave: () => void;
  onBack: () => void;
  onActivate: () => void;
  onPause: () => void;
  isSaving: boolean;
  isDirty: boolean;
  isValid: boolean;
  validationErrors: string[];
  /**
   * Há problema estrutural apontado pelo servidor. Trava Salvar e Ativar —
   * separado de `isValid` porque a lista local (`builder.validationErrors`) só
   * é recalculada quando alguém chama `builder.validate()`, e travar o Salvar
   * por uma lista velha deixaria o botão morto para sempre (o próprio Salvar é
   * quem recalcularia). A lista do servidor é refeita a cada edição.
   */
  temErroServidor: boolean;
  /** Conferência no servidor em andamento (mostra "Conferindo..." no painel). */
  isConferindo: boolean;
  /** Veio resposta boa do servidor para o desenho atual. */
  conferido: boolean;
  /**
   * A conferência falhou. Sem isso o painel mostraria o verde "Nenhum erro de
   * validação" para um desenho que NINGUÉM conferiu — o oposto do que a tela
   * existe para fazer.
   */
  conferenciaFalhou: boolean;
  forceShowErrors?: boolean;
  onErrorsShown?: () => void;
  onHistoryClick?: () => void;
  isHistoryOpen?: boolean;
  onTestClick?: () => void;
  isTestOpen?: boolean;
  isTestRunning?: boolean;
  onAIClick?: () => void;
  isAIOpen?: boolean;
}> = ({
  workflow,
  mode,
  name,
  onNameChange,
  onSave,
  onBack,
  onActivate,
  onPause,
  isSaving,
  isDirty,
  isValid,
  validationErrors,
  temErroServidor,
  isConferindo,
  conferido,
  conferenciaFalhou,
  forceShowErrors,
  onErrorsShown,
  onHistoryClick,
  isHistoryOpen,
  onTestClick,
  isTestOpen,
  isTestRunning,
  onAIClick,
  isAIOpen,
}) => {
  const [showErrors, setShowErrors] = useState(false);

  // Abrir dropdown automaticamente quando forceShowErrors for true
  useEffect(() => {
    if (forceShowErrors && validationErrors.length > 0) {
      setShowErrors(true);
      onErrorsShown?.();
    }
  }, [forceShowErrors, validationErrors.length, onErrorsShown]);

  // ────────────────────────────────────────────────────────────────────────
  // O painel de Validação mora FORA da barra (conserto final, 2026-08-27).
  //
  // A barra rola na horizontal em telas estreitas (`overflow-x-auto`), e
  // `overflow` diferente de `visible` RECORTA qualquer descendente
  // posicionado: o painel ia de y=55 a y=125 numa caixa de 55px de altura
  // visível e o navegador simplesmente não o pintava. `z-index` não escapa de
  // recorte — medido com `elementFromPoint`, que devolvia o canvas nos três
  // pontos do painel. E como esta entrega passou a DESABILITAR o Salvar, o
  // resultado ficava pior que antes: botão cinza com a explicação escondida.
  //
  // Correção: um invólucro sem recorte por fora da barra; a barra continua
  // rolando. O `deslocamentoPainel` mantém o painel colado ao botão, medindo a
  // distância entre a borda direita do invólucro e a do botão "Validação".
  // ────────────────────────────────────────────────────────────────────────
  const involucroRef = useRef<HTMLDivElement>(null);
  const botaoValidacaoRef = useRef<HTMLDivElement>(null);
  const [deslocamentoPainel, setDeslocamentoPainel] = useState(16);

  useEffect(() => {
    if (!showErrors) return;
    const recalcular = (): void => {
      const involucro = involucroRef.current;
      const botao = botaoValidacaoRef.current;
      if (!involucro || !botao) return;
      const caixaInvolucro = involucro.getBoundingClientRect();
      const caixaBotao = botao.getBoundingClientRect();
      setDeslocamentoPainel(Math.max(8, caixaInvolucro.right - caixaBotao.right));
    };
    recalcular();
    // A barra é o primeiro filho do invólucro — rolar nela move o botão.
    const barra = involucroRef.current?.firstElementChild ?? null;
    window.addEventListener('resize', recalcular);
    barra?.addEventListener('scroll', recalcular);
    return () => {
      window.removeEventListener('resize', recalcular);
      barra?.removeEventListener('scroll', recalcular);
    };
  }, [showErrors]);

  // ────────────────────────────────────────────────────────────────────────
  // Estado que o BOTÃO da barra mostra (conserto final, 2026-08-27).
  //
  // Ele usava `isValid` puro e por isso dava visto VERDE com "Workflow válido"
  // quando a conferência tinha FALHADO — a lista de problemas vem vazia por
  // falta de resposta, não por saúde. E o botão é o que o dono vê sem clicar:
  // o painel já avisava em âmbar enquanto o botão, ao lado, atestava saúde.
  // A ordem abaixo é a MESMA do painel, para os dois nunca se contradizerem.
  // ────────────────────────────────────────────────────────────────────────
  const estadoDaConferencia: {
    chave: 'erro' | 'falhou' | 'ok' | 'conferindo' | 'ocioso';
    icone: IconName;
    cor: string;
    titulo: string;
  } = validationErrors.length > 0
    ? { chave: 'erro', icone: 'alert-circle', cor: 'var(--theme-error-color)', titulo: 'Erros de validação' }
    : conferenciaFalhou
      ? { chave: 'falhou', icone: 'help-circle', cor: 'var(--theme-warning-color)', titulo: 'Não consegui conferir o desenho' }
      : conferido
        ? { chave: 'ok', icone: 'check-circle', cor: 'var(--theme-success-color)', titulo: 'Workflow válido' }
        : isConferindo
          ? { chave: 'conferindo', icone: 'loader', cor: 'var(--theme-card-muted)', titulo: 'Conferindo o desenho...' }
          : { chave: 'ocioso', icone: 'help-circle', cor: 'var(--theme-card-muted)', titulo: 'Ainda não há desenho para conferir' };

  // Estado "ativo" (painel aberto) reutilizado pelos botões-toggle da barra —
  // tinta roxa derivada do primário do tema (white-label safe).
  const activeToggleStyle: React.CSSProperties = {
    backgroundColor: 'color-mix(in srgb, var(--theme-primary) 12%, transparent)',
    borderColor: 'transparent',
    color: 'var(--theme-primary)',
  };
  const sectionLabel = mode === 'skill' ? 'Skills' : 'Workflows';

  return (
    // Invólucro SEM recorte: a barra abaixo rola na horizontal, o painel de
    // Validação mora aqui fora para não ser recortado por ela.
    <div ref={involucroRef} className="relative flex-none">
      <div
        className="flex items-center gap-2 sm:gap-3 h-14 px-2 sm:px-4 border-b overflow-x-auto"
        style={{
          backgroundColor: 'var(--theme-card-background)',
          borderColor: 'var(--theme-card-border)',
        }}
      >
        {/* Zona 1 — breadcrumb shared (F25-F31: contrato DS, nunca seta-voltar) + identidade + nome.
            O segmento final é a seção (Workflows/Skills) — o "nome" da entidade é o Input editável ao lado. */}
        <div className="flex-none min-w-0">
          <Breadcrumb
            items={[
              { label: 'Automação', onNavigate: onBack, testid: 'workflow-editor-back' },
              { label: sectionLabel, testid: 'workflow-editor-breadcrumb-current' },
            ]}
            mobileLegend="Automação"
            data-testid="workflow-editor-breadcrumb"
          />
        </div>

        <span
          className="inline-flex items-center justify-center rounded-lg flex-none"
          style={{
            width: 30,
            height: 30,
            backgroundColor: 'color-mix(in srgb, var(--theme-primary) 12%, transparent)',
            color: 'var(--theme-primary)',
          }}
          aria-hidden="true"
        >
          <Icon name={mode === 'skill' ? 'git-branch' : 'zap'} className="h-4 w-4" />
        </span>

        <Input
          type="text"
          data-testid="workflow-editor-name"
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          fullWidth={false}
          className="bg-transparent font-bold text-base focus:ring-0 w-[130px] sm:w-[280px] flex-none px-2"
          placeholder="Nome do workflow"
        />

        {isDirty && (
          <span
            className="inline-flex items-center gap-1.5 h-6 px-2.5 rounded-full flex-none"
            style={{
              fontSize: 11.5,
              fontWeight: 700,
              backgroundColor: 'color-mix(in srgb, var(--theme-warning-color) 15%, transparent)',
              color: 'var(--theme-warning-color)',
            }}
          >
            <Icon name="alert-circle" className="h-2.5 w-2.5 flex-none" />
            <span className="hidden md:inline">Alterações não salvas</span>
          </span>
        )}

        {/* Zona 2 — ações (único primário roxo: Salvar) */}
        <div className="ml-auto flex items-center gap-2 flex-none">
          {/* AI button */}
          {onAIClick && (
            <Button
              variant="secondary"
              size="md"
              onClick={onAIClick}
              data-testid="workflow-editor-ai"
              title="Assistente IA"
              style={isAIOpen ? activeToggleStyle : undefined}
            >
              <Icon name="cpu" className="h-4 w-4" />
              <span className="hidden lg:inline">IA</span>
            </Button>
          )}

          {/* Test button */}
          {onTestClick && (
            <Button
              variant="secondary"
              size="md"
              onClick={onTestClick}
              data-testid="workflow-editor-test"
              title="Testar workflow"
              style={isTestOpen || isTestRunning ? activeToggleStyle : undefined}
            >
              <Icon name="activity" className={`h-4 w-4 ${isTestRunning ? 'animate-pulse' : ''}`} />
              <span className="hidden lg:inline">Testar</span>
            </Button>
          )}

          {/* History button */}
          {onHistoryClick && (
            <Button
              variant="secondary"
              size="md"
              onClick={onHistoryClick}
              data-testid="workflow-editor-history"
              title="Histórico de execuções"
              style={isHistoryOpen ? activeToggleStyle : undefined}
            >
              <Icon name="clock" className="h-4 w-4" />
              <span className="hidden lg:inline">Histórico</span>
            </Button>
          )}

          {/* Validation status — o painel fica FORA da barra (ver comentário do
              invólucro): aqui dentro ele seria recortado pelo overflow-x-auto. */}
          <div ref={botaoValidacaoRef}>
            <Button
              variant="secondary"
              size="md"
              onClick={() => setShowErrors(!showErrors)}
              data-testid="workflow-editor-validation"
              data-estado={estadoDaConferencia.chave}
              title={estadoDaConferencia.titulo}
            >
              <span className="inline-flex" style={{ color: estadoDaConferencia.cor }}>
                <Icon
                  name={estadoDaConferencia.icone}
                  className={`h-4 w-4 ${estadoDaConferencia.chave === 'conferindo' ? 'animate-spin' : ''}`}
                />
              </span>
              <span className="hidden lg:inline">Validação</span>
            </Button>
          </div>

          {/* Selo de pausa automática (M1, spec §5.8) — ao lado do botão Ativar/Pausar */}
          {workflow?.autoPause && (
            <WorkflowStatusBadge
              status={workflow.status}
              autoPause={workflow.autoPause}
              data-testid="workflow-editor-status"
            />
          )}

          {/* Activate / Pause */}
          {workflow?.status === 'active' ? (
            <Button variant="secondary" size="md" onClick={onPause} data-testid="workflow-editor-pause">
              <Icon name="pause" className="h-4 w-4" />
              <span className="hidden md:inline">Pausar</span>
            </Button>
          ) : (
            <Button
              variant="secondary"
              size="md"
              onClick={onActivate}
              disabled={!isValid || !workflow}
              data-testid="workflow-editor-activate"
            >
              <Icon name="play" className="h-4 w-4" />
              <span className="hidden md:inline">Ativar</span>
            </Button>
          )}

          {/* Salvar — único primário roxo (texto só no sm+; mobile = só ícone) */}
          <Button variant="primary" size="md" onClick={onSave} disabled={isSaving || !isDirty || temErroServidor || isConferindo} data-testid="workflow-editor-save">
            <Icon name="save" className="h-4 w-4" />
            <span className="hidden sm:inline">{isSaving ? 'Salvando...' : 'Salvar'}</span>
          </Button>
        </div>
      </div>
      {/* Painel de Validação — FORA da barra que rola (ver o comentário do
          invólucro). `top: 100%` + `right` medido deixam ele colado ao botão. */}
      {showErrors && (
        <div
          className="absolute w-72 rounded-xl shadow-lg border p-3 z-50"
          data-testid="workflow-editor-validation-panel"
          style={{
            top: '100%',
            right: deslocamentoPainel,
            marginTop: 6,
            backgroundColor: '#ffffff',
            borderColor: '#e5e7eb',
          }}
        >
          <div className="flex items-center justify-between mb-2">
            <span
              className="font-semibold text-sm"
              style={{ color: validationErrors.length > 0 ? '#dc2626' : '#0f9d6b' }}
            >
              {validationErrors.length > 0 ? 'Erros de validação' : 'Validação'}
            </span>
            <button
              onClick={() => setShowErrors(false)}
              data-testid="workflow-editor-validation-close"
              style={{ color: '#64748b' }}
            >
              <Icon name="x" className="h-4 w-4" />
            </button>
          </div>
          {validationErrors.length > 0 ? (
            <ul className="space-y-1" data-testid="workflow-editor-validation-list">
              {validationErrors.map((error, index) => (
                <li
                  key={index}
                  className="text-xs flex items-start gap-1.5"
                  data-testid="workflow-editor-validation-error"
                  style={{ color: '#475569' }}
                >
                  <span style={{ color: '#ef4444' }}>•</span>
                  {error}
                </li>
              ))}
            </ul>
          ) : conferenciaFalhou ? (
            /* Falhou = ninguém conferiu. NUNCA mostrar o verde aqui. */
            <div
              className="flex items-start gap-2 text-xs font-semibold"
              data-testid="workflow-editor-validation-offline"
              style={{ color: '#b45309' }}
            >
              <Icon name="alert-circle" className="h-4 w-4 flex-none" />
              Não consegui conferir agora — verifique sua conexão.
            </div>
          ) : conferido ? (
            <div className="flex items-center gap-2 text-xs font-semibold" style={{ color: '#0f9d6b' }}>
              <Icon name="check-circle" className="h-4 w-4" />
              Nenhum erro de validação
            </div>
          ) : isConferindo ? (
            <div
              className="text-xs"
              data-testid="workflow-editor-validation-checking"
              style={{ color: '#64748b' }}
            >
              Conferindo o desenho...
            </div>
          ) : (
            <div className="text-xs" data-testid="workflow-editor-validation-idle" style={{ color: '#64748b' }}>
              Ainda não há desenho para conferir.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Main editor component (with React Flow context)
 */
interface WorkflowEditorInnerProps {
  mode: 'workflow' | 'skill';
}

const WorkflowEditorInner: React.FC<WorkflowEditorInnerProps> = ({ mode }) => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const reactFlowWrapper = useRef<HTMLDivElement>(null);
  const { project, getViewport, setViewport } = useReactFlow();

  // Get folderId from query params (for new workflows)
  const folderIdFromUrl = searchParams.get('folderId');
  // Params de URL (?name= / ?trigger=) — pré-semeiam nome + nó-gatilho. Eram do drawer "Novo workflow" (#237), removido em 2026-09-20 (D18); seguem usados pelos testes de navegador.
  const nameFromUrl = searchParams.get('name');
  const triggerFromUrl = searchParams.get('trigger');

  // Quando acessamos /automation/workflows/new, o id é undefined
  // Quando acessamos /automation/workflows/:id, o id tem o valor do parâmetro
  const isNewWorkflow = !id;

  // Fetch existing workflow
  const { data: workflow, isLoading } = useWorkflow(isNewWorkflow ? undefined : id);

  // Form state
  const [name, setName] = useState(mode === 'skill' ? 'Novo Skill' : 'Novo Workflow');
  const [description, setDescription] = useState('');

  // Builder hook
  const builder = useWorkflowBuilder({
    initialDefinition: workflow?.definition,
    onChange: () => {}, // Will update dirty state automatically
    onConnectionBlocked: (reason) => {
      toast.error(reason, { duration: 4000 });
    },
  });

  // ------------------------------------------------------------------
  // Conferência estrutural no servidor (2026-08-27)
  // ------------------------------------------------------------------
  // O que vai para o servidor é a MESMA definição que o Salvar manda (nós já
  // convertidos por `getDefinition`), menos a área visível do canvas: incluir o
  // `viewport` faria cada arrasto/zoom virar requisição.
  const definicaoParaConferir = useMemo<WorkflowDefinition>(() => {
    const { nodes, edges } = builder.getDefinition();
    return { nodes, edges, variables: {} };
  }, [builder.nodes, builder.edges, builder.getDefinition]);
  const {
    issues: issuesServidor,
    isValidating,
    conferido,
    conferenciaFalhou,
  } = useValidateWorkflow(definicaoParaConferir);

  // Erros locais (instantâneos: campos obrigatórios, horário, contexto) somados
  // aos estruturais do servidor. O painel mostra só o texto; os `nodeIds` são o
  // que pinta o nó culpado de vermelho no canvas.
  const errosParaExibir = useMemo(
    () => [...builder.validationErrors, ...issuesServidor.map((i) => i.message)],
    [builder.validationErrors, issuesServidor],
  );
  const nosComErro = useMemo(
    () => new Set<string>([...builder.errorNodeIds, ...issuesServidor.flatMap((i) => i.nodeIds)]),
    [builder.errorNodeIds, issuesServidor],
  );
  const temErro = errosParaExibir.length > 0;
  // Só o que veio do servidor trava os botões — ver o comentário de
  // `temErroServidor` no EditorToolbar (a lista local fica velha e travaria o
  // Salvar de vez).
  const temErroServidor = issuesServidor.length > 0;

  // Mutations
  const createWorkflow = useCreateWorkflow();
  const updateWorkflow = useUpdateWorkflow();
  const activateWorkflow = useActivateWorkflow();
  const pauseWorkflow = usePauseWorkflow();

  // Selected node for configuration
  const [configuringNode, setConfiguringNode] = useState<string | null>(null);

  // Nó cuja modal "Visualizar contexto" está aberta. Enquanto ela estiver no
  // ar, a tecla Delete não apaga nada por baixo dela.
  const [runInputsNodeId, setRunInputsNodeId] = useState<string | null>(null);

  // State para forçar abertura do dropdown de erros
  const [forceShowErrors, setForceShowErrors] = useState(false);

  // Execution history states
  const [showExecutionHistory, setShowExecutionHistory] = useState(false);
  const [selectedExecution, setSelectedExecution] = useState<WorkflowExecutionResponse | null>(null);

  // Trigger preview states
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewTriggerConfig, setPreviewTriggerConfig] = useState<{
    triggerType: 'trigger_inactivity' | 'trigger_date_field';
    config: Record<string, unknown>;
  } | null>(null);
  // Node selector popup (opens on double-click or "+" button)
  const [showNodeSelector, setShowNodeSelector] = useState(false);
  // Origem do "+": quando o seletor é aberto pelo "+" de um node, o novo node
  // herda a conexão a partir deste node/handle. Null quando aberto por
  // double-click no canvas (sem conexão automática).
  const [plusSource, setPlusSource] = useState<{ nodeId: string; handle?: string } | null>(null);
  // AI panel sidebar
  const [showAIPanel, setShowAIPanel] = useState(false);
  // Workflow test states
  const [showTestPanel, setShowTestPanel] = useState(false);
  const [isStartingTest, setIsStartingTest] = useState(false);
  const [showTestDataModal, setShowTestDataModal] = useState(false);
  const {
    isConnected: isTestSocketConnected,
    testProgress,
    nodeStatuses,
    joinTestRoom,
    resetTest,
  } = useWorkflowTestSocket();

  // Query client for cache invalidation
  const queryClient = useQueryClient();

  // Auto-refresh execution history when test completes
  useEffect(() => {
    if (testProgress.status === 'completed' || testProgress.status === 'failed') {
      // Invalidate execution list cache to refresh history
      if (id) {
        queryClient.invalidateQueries({
          queryKey: executionKeys.list(id),
        });
      }
    }
  }, [testProgress.status, id, queryClient]);

  // Auto-insert skill_input node when creating a new skill
  const skillInputInsertedRef = useRef(false);
  useEffect(() => {
    if (
      mode === 'skill' &&
      isNewWorkflow &&
      !skillInputInsertedRef.current &&
      builder.nodes.length === 0
    ) {
      builder.addNode('skill_input', { x: 250, y: 200 });
      skillInputInsertedRef.current = true;
    }
  }, [mode, isNewWorkflow, builder]);

  // Workflow aberto com ?trigger= na URL — pré-semeia o nó-gatilho
  // e o nome, em vez de abrir o seletor de nós em branco.
  const triggerSeededRef = useRef(false);
  useEffect(() => {
    if (
      mode !== 'skill' &&
      isNewWorkflow &&
      triggerFromUrl &&
      !triggerSeededRef.current &&
      builder.nodes.length === 0
    ) {
      if (triggerFromUrl.startsWith('event:')) {
        const eventType = triggerFromUrl.slice('event:'.length);
        builder.addNode('trigger_event', { x: 250, y: 200 }, { config: { eventType } });
      } else {
        builder.addNode(triggerFromUrl, { x: 250, y: 200 });
      }
      if (nameFromUrl) setName(nameFromUrl);
      triggerSeededRef.current = true;
    }
  }, [mode, isNewWorkflow, triggerFromUrl, nameFromUrl, builder]);

  // Open popup by default when workflow is new/empty — exceto quando veio de
  // uma URL com ?trigger= (o nó-gatilho já será semeado acima).
  useEffect(() => {
    if (isNewWorkflow && builder.nodes.length === 0 && !triggerFromUrl) {
      setShowNodeSelector(true);
    }
  }, [isNewWorkflow, builder.nodes.length, triggerFromUrl]);

  // Load workflow data when fetched
  useEffect(() => {
    if (workflow) {
      setName(workflow.name);
      setDescription(workflow.description || '');
      builder.loadDefinition(workflow.definition);

      // Apply saved viewport programmatically after loading
      if (workflow.definition.viewport) {
        // Small delay to ensure React Flow is ready
        setTimeout(() => {
          setViewport(workflow.definition.viewport!);
        }, 100);
      }
    }
  }, [workflow, setViewport]);

  // Handle drag over (for node palette)
  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  // Handle drop (add node from palette)
  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();

      const type = event.dataTransfer.getData('application/reactflow');
      if (!type || !reactFlowWrapper.current) return;

      const bounds = reactFlowWrapper.current.getBoundingClientRect();
      const position = project({
        x: event.clientX - bounds.left,
        y: event.clientY - bounds.top,
      });

      const newNode = builder.addNode(type, position);

      // Auto-open configurator for the new node
      setConfiguringNode(newNode.id);
      builder.setSelectedNode(newNode);
    },
    [builder, project]
  );

  // Handle node click
  const onNodeClick = useCallback((_: any, node: any) => {
    // Close execution history and test panel when clicking on a node (sidebar exclusivity)
    setShowExecutionHistory(false);
    setSelectedExecution(null);
    setShowTestPanel(false);
    setConfiguringNode(node.id);
    builder.setSelectedNode(node);
    builder.setSelectedEdge(null); // Clear edge selection when selecting a node
  }, [builder]);

  // Handle edge click (select edge for deletion)
  const onEdgeClick = useCallback((_: any, edge: any) => {
    // Close configurator when clicking on an edge
    setConfiguringNode(null);
    builder.setSelectedNode(null);
    builder.setSelectedEdge(edge);
  }, [builder]);

  // Handle pane click (deselect)
  const onPaneClick = useCallback(() => {
    setConfiguringNode(null);
    // Solta também a modal "Visualizar contexto": a exclusão sai cedo enquanto
    // esse sinal tiver valor, e clicar no quadro vazio é o gesto que já desfaz
    // seleção e fecha o painel do nó. Sem esta linha, enquanto a modal não
    // existir, abrir o contexto deixaria a tecla Delete e o "Excluir" do menu
    // mudos até recarregar a página. Com a modal no ar, ela cobre o quadro com
    // o próprio véu e este clique nem acontece.
    setRunInputsNodeId(null);
    builder.clearSelection();
  }, [builder]);

  // Handle double-click on canvas — open NodeSelector only if clicking on empty pane
  const onPaneDoubleClick = useCallback((event: React.MouseEvent) => {
    // Check if the double-click target is a node (not empty pane)
    const target = event.target as HTMLElement;
    if (target.closest('.react-flow__node')) return;

    setConfiguringNode(null);
    builder.clearSelection();
    setPlusSource(null); // double-click no canvas → sem conexão automática
    setShowNodeSelector(true);
  }, [builder]);

  // ------------------------------------------------------------------
  // Excluir: UMA função para o menu do nó e para a tecla (spec §5.2)
  // ------------------------------------------------------------------
  /** Confirmação em andamento — ver o comentário da trava logo abaixo. */
  const deleteInFlightRef = useRef(false);

  const requestDelete = useCallback(
    async (target?: { nodeId: string }): Promise<void> => {
      // O ConfirmProvider guarda UM resolvedor: uma segunda confirmação
      // sobrescreve o da primeira, e o `await` dela nunca termina. Sem esta
      // trava, dois Delete seguidos deixam a exclusão presa para sempre.
      if (deleteInFlightRef.current) return;
      // Nada de apagar por baixo de uma superfície aberta.
      if (showNodeSelector || runInputsNodeId !== null) return;

      const nodeIds = target !== undefined
        ? [target.nodeId]
        : configuringNode !== null
          ? [configuringNode]
          : builder.nodes.filter((node) => node.selected).map((node) => node.id);

      if (nodeIds.length === 0) {
        // Só ligação selecionada: sai sem confirmação — não há o que perder
        // além da linha. As DUAS fontes contam: o sinal do React Flow (que a
        // seleção por retângulo usa) e a ligação clicada, guardada pelo builder.
        const edgeIds = new Set(builder.edges.filter((edge) => edge.selected).map((edge) => edge.id));
        if (builder.selectedEdge) edgeIds.add(builder.selectedEdge.id);
        for (const edgeId of edgeIds) builder.deleteEdge(edgeId);
        return;
      }

      const first = builder.nodes.find((node) => node.id === nodeIds[0]);
      const firstLabel = getNodeDisplayLabel(first);

      deleteInFlightRef.current = true;
      try {
        const confirmed = await confirm({
          title: nodeIds.length === 1 ? `Excluir o nó "${firstLabel}"?` : `Excluir ${nodeIds.length} nós?`,
          message: nodeIds.length === 1
            ? 'As ligações dele também serão removidas.'
            : 'As ligações deles também serão removidas.',
          confirmButtonText: 'Excluir',
          variant: 'danger',
          // Sem isto a caixa nasce em z-[60], ATRÁS do painel do nó (z-[201]),
          // e o editor parece travado.
          layer: 'editor',
        });
        if (!confirmed) return;
        builder.deleteNodes(nodeIds);
        // Fecha o painel SÓ se quem estava aberto foi junto. Excluir o nó X
        // pelo menu DELE não pode fechar o painel do nó Y, que continua no
        // quadro — e a exclusão pelo menu sempre pode receber um alvo que não
        // é o nó configurado.
        if (configuringNode !== null && nodeIds.includes(configuringNode)) {
          setConfiguringNode(null);
        }
        // A ligação selecionada NÃO é zerada aqui: quem já faz isso, e na
        // medida certa, é o `deleteNodes` do builder — ele solta a seleção
        // apenas quando a ligação tocava um nó excluído (que é exatamente
        // quando ela deixa de existir). Zerar de fora, sem condição, tirava a
        // seleção de uma ligação que continuou no quadro.
      } finally {
        deleteInFlightRef.current = false;
      }
    },
    [
      builder.nodes,
      builder.edges,
      builder.selectedEdge,
      builder.deleteEdge,
      builder.deleteNodes,
      configuringNode,
      confirm,
      runInputsNodeId,
      showNodeSelector,
    ],
  );

  // Tecla Delete/Backspace: MESMA função do menu.
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Delete' && event.key !== 'Backspace') return;
      if (isDeleteKeySilenced(event)) return;
      // Antes de qualquer `await`: depois dele o navegador já processou a tecla.
      event.preventDefault();
      void requestDelete();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [requestDelete]);

  // Callbacks do menu do nó. `useCallback` porque eles entram no `data` de CADA
  // nó dentro do memo `nodesWithErrors`, e o BaseNode é `memo` sem comparador:
  // função nova a CADA render re-renderizaria o canvas inteiro.
  // `handleViewRunInputs` é constante (deps `[]`). `handleRequestDelete` NÃO é:
  // acompanha `requestDelete`, que muda com os nós/ligações (o memo já dependia
  // de `builder.nodes`) e quando o painel, o seletor ou a modal de contexto
  // abrem/fecham. São eventos discretos do usuário — recalcular o memo aí é
  // aceitável, e trocar os sinalizadores por refs traria risco no caminho que
  // decide O QUE é apagado.
  const handleRequestDelete = useCallback(
    (nodeId: string) => { void requestDelete({ nodeId }); },
    [requestDelete],
  );

  const handleViewRunInputs = useCallback((nodeId: string) => {
    // A modal abre POR CIMA de tudo; painel e seletor saem da frente.
    setConfiguringNode(null);
    setShowNodeSelector(false);
    setPlusSource(null);
    setRunInputsNodeId(nodeId);
  }, []);

  // Save workflow
  const handleSave = async () => {
    // Confere o que é local (síncrono) e o que o servidor já apontou. Qualquer
    // um dos dois abre o painel de Validação em vez de salvar.
    const localOk = builder.validate();
    if (!localOk || issuesServidor.length > 0) {
      setForceShowErrors(true);
      return;
    }

    // Get current viewport to save with workflow
    const currentViewport = getViewport();
    const definition = builder.getDefinition(currentViewport);

    try {
      if (isNewWorkflow) {
        const data: CreateWorkflowRequest = {
          name,
          description: description || undefined,
          definition,
          status: 'draft',
          folderId: folderIdFromUrl || undefined, // Include folderId if creating from folder
          usageType: mode === 'skill' ? 'skill' : 'automation',
        };
        const created = await createWorkflow.mutateAsync(data);
        const basePath = mode === 'skill' ? '/automation/skills' : '/automation/workflows';
        navigate(`${basePath}/${created.id}`, { replace: true });
      } else if (id) {
        const data: UpdateWorkflowRequest = {
          name,
          description: description || undefined,
          definition,
        };
        const updated = await updateWorkflow.mutateAsync({ id, data });
        // Recarrega a definição retornada (traz o webhookId gerado no servidor)
        builder.loadDefinition(updated.definition);
      }
      builder.setIsDirty(false);
    } catch (error) {
      console.error('Error saving workflow:', error);
    }
  };

  // Activate workflow
  const handleActivate = async () => {
    if (id && !isNewWorkflow) {
      try {
        await activateWorkflow.mutateAsync(id);
      } catch (error) {
        console.error('Error activating workflow:', error);
      }
    }
  };

  // Pause workflow
  const handlePause = async () => {
    if (id && !isNewWorkflow) {
      try {
        await pauseWorkflow.mutateAsync(id);
      } catch (error) {
        console.error('Error pausing workflow:', error);
      }
    }
  };

  // Handle preset selection from NodeSelector
  const handlePresetSelect = useCallback((preset: NodePreset) => {
    // Quando aberto pelo "+" de um node, posiciona o novo node à direita do
    // node de origem e herda a conexão. Senão (double-click), centro do viewport.
    const sourceNode = plusSource ? builder.nodes.find((n) => n.id === plusSource.nodeId) : undefined;
    let position: { x: number; y: number };
    if (sourceNode) {
      position = {
        x: sourceNode.position.x + 320,
        // O "+" da saída vermelha ("Cancelado" do Aguardar, "Falha" do
        // Transferir conversa) desce o nó novo: o lado verde já ocupa este x
        // (spec 2026-09-20 §7.2).
        y: sourceNode.position.y
          + (plusSource?.handle === WORKFLOW_WAIT_CANCELLED_HANDLE || plusSource?.handle === WORKFLOW_FAILURE_HANDLE
            ? CANCELLED_BRANCH_Y_OFFSET
            : 0),
      };
    } else {
      const bounds = reactFlowWrapper.current?.getBoundingClientRect();
      position = bounds
        ? project({ x: bounds.width / 2, y: bounds.height / 2 })
        : { x: 400, y: 300 };
    }

    const newNode = builder.addNode(preset.nodeType, position, { label: preset.label, ...(preset.defaultConfig ? { config: preset.defaultConfig } : {}) });

    // Herda a conexão do node de origem (bug: "+" adicionava node solto).
    if (plusSource) {
      builder.connectNodes(plusSource.nodeId, newNode.id, plusSource.handle);
    }

    setPlusSource(null);
    setShowNodeSelector(false);
    setConfiguringNode(newNode.id);
    builder.setSelectedNode(newNode);
  }, [builder, project, plusSource]);

  // Handle node configuration update
  const handleNodeUpdate = (nodeId: string, data: any) => {
    // Pergunta (2026-09-28): apagar uma opção (ou trocar o tipo de resposta)
    // tira a saída dela do nó. A ligação que saía dali apontaria para uma saída
    // que não existe mais (o Salvar recusaria com PERGUNTA_SAIDA_INVALIDA) —
    // sai junto, com aviso.
    const node = builder.nodes.find((n) => n.id === nodeId);
    if (node?.data?.type === 'action_ask_question') {
      const antes = askQuestionOutputHandles((node.data.config ?? {}) as Record<string, unknown>);
      const depois = new Set(askQuestionOutputHandles((data?.config ?? {}) as Record<string, unknown>));
      const ligadas = antes.filter(
        (handle) => !depois.has(handle) && builder.edges.some((edge) => edge.source === nodeId && edge.sourceHandle === handle),
      );
      for (const handle of ligadas) builder.removeEdgesFromHandle(nodeId, handle);
      if (ligadas.length > 0) {
        toast.info('A saída removida da pergunta tinha uma ligação — ela foi apagada junto.');
      }
    }
    builder.updateNode(nodeId, data);
  };

  /**
   * Troca de regras da aba Cancelamento (spec 2026-09-20 §7.2).
   *
   * Apagar a ÚLTIMA regra de um Aguardar tira a saída "Cancelado" do nó. Se
   * houver algo ligado nela, a ligação passaria a apontar para uma saída que
   * não existe mais — e o desenho ficaria recusado no Salvar
   * (`WAIT_FOR_CANCELADO_SEM_REGRA`). Por isso pergunta antes e, com o "sim",
   * remove a ligação junto. Nos demais casos é a gravação de sempre.
   */
  const handleCancellationChange = useCallback(
    async (nodeId: string, next: WorkflowNodeCancellation | undefined): Promise<void> => {
      const node = builder.nodes.find((n) => n.id === nodeId);
      if (!node) return;

      const hadRules = (node.data?.cancellation?.rules?.length ?? 0) > 0;
      const keepsRules = (next?.rules.length ?? 0) > 0;
      const wired = builder.edges.some(
        (edge) => edge.source === nodeId && edge.sourceHandle === WORKFLOW_WAIT_CANCELLED_HANDLE,
      );

      if (node.data?.type === 'control_wait_for' && hadRules && !keepsRules && wired) {
        const confirmed = await confirm({
          title: 'Remover a saída Cancelado deste Aguardar?',
          message:
            'Sem regra própria o Aguardar volta a ter uma saída só. A ligação que sai da saída Cancelado será removida e os nós pendurados nela ficam soltos.',
          confirmButtonText: 'Remover',
          variant: 'danger',
          // Sem isto a caixa nasce em z-[60], ATRÁS do painel do nó (z-[201]).
          layer: 'editor',
        });
        if (!confirmed) return;
        builder.removeEdgesFromHandle(nodeId, WORKFLOW_WAIT_CANCELLED_HANDLE);
      }

      builder.updateNode(nodeId, { ...node.data, cancellation: next });
    },
    [builder, confirm],
  );

  // Close configurator
  const handleCloseConfigurator = () => {
    setConfiguringNode(null);
    builder.clearSelection();
  };

  // Handle history sidebar toggle
  const handleHistoryClick = () => {
    setShowExecutionHistory(!showExecutionHistory);
    if (!showExecutionHistory) {
      setConfiguringNode(null);
      setShowTestPanel(false);
      setShowAIPanel(false);
      builder.clearSelection();
    }
  };

  // Handle test panel toggle
  const handleAIClick = () => {
    setShowAIPanel(!showAIPanel);
    if (!showAIPanel) {
      setConfiguringNode(null);
      setShowTestPanel(false);
      setShowExecutionHistory(false);
      setSelectedExecution(null);
      builder.clearSelection();
    }
  };

  const handleTestClick = () => {
    setShowTestPanel(!showTestPanel);
    if (!showTestPanel) {
      // Close node configurator, history, and AI panel when opening test panel (sidebar exclusivity)
      setConfiguringNode(null);
      setShowExecutionHistory(false);
      setShowAIPanel(false);
      setSelectedExecution(null);
      builder.clearSelection();
    }
  };

  // Handle opening test modal to select test data
  const handleOpenTestModal = () => {
    if (!id || isNewWorkflow || !isTestSocketConnected) return;

    // Validate workflow before testing
    const isValid = builder.validate();
    if (!isValid) {
      setForceShowErrors(true);
      return;
    }

    // Open modal to select test data
    setShowTestDataModal(true);
  };

  // Handle starting a workflow test with selected test context
  const handleStartTest = async (testContext: TestContext, triggerNodeId?: string) => {
    if (!id || isNewWorkflow || !isTestSocketConnected) return;

    setShowTestDataModal(false);
    setIsStartingTest(true);
    try {
      // RACE CONDITION FIX: Generate executionId FIRST, join room BEFORE API call
      // This ensures we're subscribed to events BEFORE the backend starts emitting them
      // Generate ObjectId-compatible 24-character hex string (NOT UUID which is 36 chars)
      const executionId = Array.from(crypto.getRandomValues(new Uint8Array(12)))
        .map(b => b.toString(16).padStart(2, '0'))
        .join('');

      // Join the socket room FIRST to receive real-time updates
      joinTestRoom(id, executionId);

      // Then start the test, passing the pre-generated executionId and testContext
      await workflowsApi.testWorkflow(id, {
        executionId,
        testContext,
        ...(triggerNodeId !== undefined ? { triggerNodeId } : {}),
      });
    } catch (error) {
      console.error('Error starting workflow test:', error);
    } finally {
      setIsStartingTest(false);
    }
  };

  // Handle reset test
  const handleResetTest = () => {
    resetTest();
  };

  // Handle close test panel
  const handleCloseTestPanel = () => {
    setShowTestPanel(false);
  };

  // Trigger preview counts per node
  const [previewCounts, setPreviewCounts] = useState<Record<string, number>>({});

  // Compute a stable key for trigger node configs to detect changes
  const triggerConfigKey = useMemo(() => {
    return builder.nodes
      .filter(
        (n) =>
          (n.data?.type === 'trigger_inactivity' || n.data?.type === 'trigger_date_field') &&
          n.data?.config?.entityType
      )
      .map((n) => `${n.id}:${JSON.stringify(n.data.config)}`)
      .join('|');
  }, [builder.nodes]);

  // Fetch trigger preview counts when trigger nodes change
  useEffect(() => {
    if (!triggerConfigKey) return;

    const triggerNodes = builder.nodes.filter(
      (n) =>
        (n.data?.type === 'trigger_inactivity' || n.data?.type === 'trigger_date_field') &&
        n.data?.config?.entityType
    );

    triggerNodes.forEach(async (node) => {
      try {
        const result = await workflowsApi.previewTriggerEntities({
          triggerType: node.data.type as 'trigger_inactivity' | 'trigger_date_field',
          config: node.data.config as Record<string, unknown>,
          page: 1,
          limit: 1,
        });
        setPreviewCounts((prev) => ({ ...prev, [node.id]: result.total }));
      } catch {
        // Silently ignore preview errors
      }
    });
  }, [triggerConfigKey]);

  // Handle opening preview modal from trigger node badge click
  const handlePreviewClick = useCallback((nodeId: string) => {
    const node = builder.nodes.find((n) => n.id === nodeId);
    if (!node) return;

    setPreviewTriggerConfig({
      triggerType: node.data.type as 'trigger_inactivity' | 'trigger_date_field',
      config: node.data.config as Record<string, unknown>,
    });
    setShowPreviewModal(true);
  }, [builder.nodes]);

  /**
   * Ids dos nós que o SERVIDOR já conhece — a definição salva, não o quadro.
   *
   * É o que separa "este nó pode ter contexto" de "este nó acabou de nascer
   * aqui". A lista vem do cache do detalhe do workflow, que o Salvar atualiza.
   */
  const savedNodeIds = useMemo(
    () => new Set((workflow?.definition?.nodes ?? []).map((node) => node.id)),
    [workflow],
  );

  // Add hasError flag and executionStatus to nodes for visual highlighting
  const nodesWithErrors = useMemo(() => {
    // Create a map of node executions by nodeId for quick lookup
    // Priority: test progress (real-time) > selected execution (history)
    let nodeExecutionMap: Map<string, { status?: string; error?: string; duration?: number; reason?: string }> | null = null;

    // If test is running or just completed, use real-time test status
    if (testProgress.status !== 'idle' && nodeStatuses.size > 0) {
      nodeExecutionMap = new Map(
        Array.from(nodeStatuses.entries()).map(([nodeId, info]) => [
          nodeId,
          {
            status: info.status,
            error: info.error,
            duration: info.duration,
            reason: typeof info.output?.reason === 'string' ? info.output.reason : undefined,
          },
        ])
      );
    } else if (selectedExecution?.nodeOrder && selectedExecution.runData) {
      // Otherwise, use historical execution status (runData format)
      // For each node, use the last run entry (highest runIndex) for display status.
      // Nó que NUNCA rodou (sem lastRun) fica SEM status — nenhum selo — ao
      // invés do 'skipped' fixo de antes, que pintava toda entrada de
      // `nodeOrder` como "não rodou" mesmo quando o node não tinha execução
      // nenhuma registrada.
      nodeExecutionMap = new Map(
        selectedExecution.nodeOrder.map((nodeId) => {
          const runs = selectedExecution.runData[nodeId] ?? [];
          const lastRun = runs.length > 0 ? runs[runs.length - 1] : undefined;
          return [
            nodeId,
            {
              status: lastRun?.status,
              error: lastRun?.error,
              duration: lastRun?.duration,
              reason: typeof lastRun?.output?.reason === 'string' ? lastRun.output.reason : undefined,
            },
          ];
        })
      );
    }

    return builder.nodes.map((node) => {
      const isTriggerPreviewable =
        node.data?.type === 'trigger_inactivity' || node.data?.type === 'trigger_date_field';

      return {
        ...node,
        data: {
          ...node.data,
          hasError: nosComErro.has(node.id),
          executionStatus: nodeExecutionMap?.get(node.id) || undefined,
          onPlusClick: (sourceId: string, sourceHandle?: string) => {
            setPlusSource({ nodeId: sourceId, ...(sourceHandle ? { handle: sourceHandle } : {}) });
            setShowNodeSelector(true);
          },
          // Menu do nó (•••) — spec 2026-09-20 §5.2 e §5.6.
          onRequestDelete: handleRequestDelete,
          onViewRunInputs: handleViewRunInputs,
          // Sem o motivo, o item nasce habilitado. Workflow ainda não salvo não
          // tem execução nenhuma para mostrar; e nó que ainda não está na
          // definição salva não tem como ter contexto (o servidor nem o
          // encontra).
          ...(isNewWorkflow
            ? { runInputsDisabledReason: RUN_INPUTS_DISABLED_REASON }
            : savedNodeIds.has(node.id)
              ? {}
              : { runInputsDisabledReason: RUN_INPUTS_UNSAVED_NODE_REASON }),
          ...(isTriggerPreviewable
            ? {
                previewCount: previewCounts[node.id] ?? null,
                onPreviewClick: () => handlePreviewClick(node.id),
              }
            : {}),
        },
      };
    });
  }, [builder.nodes, nosComErro, selectedExecution, testProgress.status, nodeStatuses, previewCounts, handlePreviewClick, handleRequestDelete, handleViewRunInputs, isNewWorkflow, savedNodeIds]);

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div
          className="animate-spin h-8 w-8 border-4 rounded-full"
          style={{ borderColor: 'var(--theme-primary)', borderTopColor: 'transparent' }}
        />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <EditorToolbar
        workflow={workflow || null}
        mode={mode}
        name={name}
        onNameChange={setName}
        onSave={handleSave}
        onBack={() =>
          navigate(mode === 'skill' ? '/automation/skills' : '/automation/workflows')
        }
        onActivate={handleActivate}
        onPause={handlePause}
        isSaving={createWorkflow.isPending || updateWorkflow.isPending}
        isDirty={builder.isDirty || name !== (workflow?.name || 'Novo Workflow')}
        isValid={!temErro}
        validationErrors={errosParaExibir}
        temErroServidor={temErroServidor}
        isConferindo={isValidating}
        conferido={conferido}
        conferenciaFalhou={conferenciaFalhou}
        forceShowErrors={forceShowErrors}
        onErrorsShown={() => setForceShowErrors(false)}
        onHistoryClick={!isNewWorkflow ? handleHistoryClick : undefined}
        isHistoryOpen={showExecutionHistory}
        onTestClick={!isNewWorkflow ? handleTestClick : undefined}
        isTestOpen={showTestPanel}
        isTestRunning={testProgress.isRunning}
        onAIClick={handleAIClick}
        isAIOpen={showAIPanel}
      />

      <div className="flex-1 flex overflow-hidden">
        {/* Node Selector Popup (centered modal) */}
        {showNodeSelector && (
          <NodeSelector
            isSkillMode={mode === 'skill'}
            triggersOnly={!builder.nodes.some((n) => n.data?.type?.startsWith('trigger_') || n.data?.type === 'skill_input')}
            onSelect={handlePresetSelect}
            onClose={() => { setShowNodeSelector(false); setPlusSource(null); }}
          />
        )}

        {/* Canvas */}
        <div className="flex-1" ref={reactFlowWrapper} data-testid="workflow-canvas">
          <ReactFlow
            nodes={nodesWithErrors}
            edges={builder.edges}
            onNodesChange={builder.onNodesChange}
            onEdgesChange={builder.onEdgesChange}
            onConnect={builder.onConnect}
            onNodeClick={onNodeClick}
            onEdgeClick={onEdgeClick}
            onPaneClick={onPaneClick}
            onDoubleClick={onPaneDoubleClick}
            zoomOnDoubleClick={false}
            /* O atalho embutido (padrão: Backspace) apaga a seleção inteira SEM
               confirmação, em paralelo com o handler da página — um furo na
               confirmação que os testes nunca pegavam, porque eles só usam
               'Delete'. `null` desliga: o React Flow não registra listener
               nenhum quando o código da tecla é nulo. */
            deleteKeyCode={null}
            onDrop={onDrop}
            onDragOver={onDragOver}
            nodeTypes={nodeTypes}
            defaultViewport={builder.initialViewport}
            snapToGrid
            snapGrid={[15, 15]}
            defaultEdgeOptions={{
              type: 'smoothstep',
              animated: true,
            }}
          >
            <Background gap={15} size={1} />
            <Controls />
            <Panel position="bottom-center" className="max-[899px]:hidden rounded-full border shadow-md text-[11.5px] whitespace-nowrap px-[15px] py-[7px]" style={{ backgroundColor: 'var(--theme-card-background)', borderColor: 'var(--theme-card-border)', color: 'var(--theme-muted-text)' }}>
              Duplo clique no canvas ou clique no <strong>+</strong> dos nodes para adicionar
            </Panel>
          </ReactFlow>
        </div>

        {/* Node Config Panel */}
        {configuringNode && (() => {
          const currentNode = builder.nodes.find((n) => n.id === configuringNode);
          if (!currentNode) return null;
          const nodeData = currentNode.data || {};
          return (
            <NodeConfigPanel
              nodeId={configuringNode}
              nodeType={nodeData.type || currentNode.type || ''}
              nodeLabel={nodeData.label || 'Node'}
              config={(nodeData.config as Record<string, unknown>) || {}}
              onChange={(newConfig) => handleNodeUpdate(configuringNode, { ...nodeData, config: newConfig })}
              onLabelChange={(label) => handleNodeUpdate(configuringNode, { ...nodeData, label })}
              onClose={handleCloseConfigurator}
              availableContext={calculateAvailableContext(builder.nodes as never, builder.edges, configuringNode)}
              cancellation={nodeData.cancellation}
              onCancellationChange={(next) => { void handleCancellationChange(configuringNode, next); }}
              filters={nodeData.filters}
              onFiltersChange={(next) => handleNodeUpdate(configuringNode, { ...nodeData, filters: next })}
              belowCancelledOutput={isBelowCancelledOutput(builder.edges, configuringNode)}
              savedVariables={savedVariablesBefore(builder.nodes as never, builder.edges, configuringNode)}
              filledLater={formFieldsFilledLater(builder.nodes as never, builder.edges, configuringNode)}
            />
          );
        })()}

        {/* Scrim mobile — fecha o bottom-sheet do painel aberto (só um abre por vez) */}
        {(showExecutionHistory || showTestPanel || showAIPanel) && (
          <div
            className="md:hidden fixed inset-0 z-[69] bg-black/40"
            onClick={() => { setShowExecutionHistory(false); setShowTestPanel(false); setShowAIPanel(false); }}
          />
        )}

        {/* Execution History Sidebar */}
        {showExecutionHistory && id && (
          <ExecutionHistorySidebar
            workflowId={id}
            nodes={builder.nodes}
            onSelectExecution={setSelectedExecution}
            selectedExecutionId={selectedExecution?.id}
            onClose={() => setShowExecutionHistory(false)}
          />
        )}

        {/* Workflow Test Panel */}
        {showTestPanel && id && (
          <WorkflowTestPanel
            testProgress={testProgress}
            nodeStatuses={nodeStatuses}
            isConnected={isTestSocketConnected}
            onClose={handleCloseTestPanel}
            onReset={handleResetTest}
            onStartTest={handleOpenTestModal}
            isStartingTest={isStartingTest}
          />
        )}

        {/* AI Workflow Builder Panel */}
        {showAIPanel && id && (
          <WorkflowAIPanel
            workflowId={id}
            currentDefinition={builder.getDefinition()}
            onDefinitionUpdate={(def) => builder.loadDefinition(def)}
            onClose={() => setShowAIPanel(false)}
          />
        )}

        {/* Workflow Test Data Modal — monta só quando aberto pra não disparar os fetches
            de conversas/leads/contatos/tickets/eventos ao abrir o editor. */}
        {showTestDataModal && (
          <WorkflowTestModal
            isOpen={showTestDataModal}
            onClose={() => setShowTestDataModal(false)}
            onConfirm={handleStartTest}
            nodes={builder.nodes}
            isStarting={isStartingTest}
          />
        )}

        {/* "Visualizar contexto" (spec 2026-09-20 §5.6) — UMA instância, montada
            só quando aberta: fechada, nenhum dos dois hooks de busca existe.
            Molde do `WorkflowTestModal`, logo acima. */}
        {runInputsNodeId && id && (() => {
          const node = builder.nodes.find((n) => n.id === runInputsNodeId);
          const label = getNodeDisplayLabel(node);
          return (
            <NodeRunInputModal
              isOpen
              onClose={() => setRunInputsNodeId(null)}
              workflowId={id}
              nodeId={runInputsNodeId}
              nodeLabel={label}
              unrecordedReason={node ? getUnrecordedNodeReason(node, builder.edges) : null}
            />
          );
        })()}

        {/* Trigger Preview Modal */}
        {previewTriggerConfig && (
          <TriggerPreviewModal
            isOpen={showPreviewModal}
            onClose={() => setShowPreviewModal(false)}
            triggerType={previewTriggerConfig.triggerType}
            config={previewTriggerConfig.config}
          />
        )}
      </div>
    </div>
  );
};

/**
 * WorkflowEditorPage with ReactFlow Provider
 */
export interface WorkflowEditorPageProps {
  mode?: 'workflow' | 'skill';
}

export const WorkflowEditorPage: React.FC<WorkflowEditorPageProps> = ({ mode = 'workflow' }) => {
  return (
    <ReactFlowProvider>
      <WorkflowEditorInner mode={mode} />
    </ReactFlowProvider>
  );
};

export default WorkflowEditorPage;
